import axios from "axios";
import { getStoredToken } from "../utils/tokenUtils";
import { installApiLoadingInterceptors } from "../utils/apiLoading";
import { API_BASE, addFallbackInterceptor } from "../config/api.config";

// Build integration base URL (exactly one /integration; use relative paths so URL is never doubled): exactly one /integration segment (avoids double /integration/integration)
const raw = (API_BASE.integration || "").replace(/\/$/, "");
const INTEGRATION_SERVICE_URL = raw.endsWith("/integration")
  ? raw
  : `${raw}/integration`;

// Create axios instance for integration service
const integrationAPI = axios.create({
  baseURL: INTEGRATION_SERVICE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add fallback interceptor for backward compatibility
addFallbackInterceptor(
  integrationAPI,
  INTEGRATION_SERVICE_URL,
  process.env.REACT_APP_INTEGRATION_SERVICE_URL || "http://localhost:5002",
);

// Add auth token to requests
integrationAPI.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

installApiLoadingInterceptors(integrationAPI);

/**
 * Integration Service for managing third-party integrations
 */
export const integrationService = {
  /**
   * Get integration status for a client
   * @param {string} clientId - Client ID
   * @returns {Promise} Integration status
   */
  getIntegrationStatus: async (clientId) => {
    const response = await integrationAPI.get(`/status?client_id=${clientId}`);
    return response.data;
  },

  /**
   * Get integration credentials for a client
   * @param {string} clientId - Client ID
   * @returns {Promise} Integration credentials
   */
  getIntegration: async (clientId) => {
    try {
      const response = await integrationAPI.get(
        `/config/get?client_id=${clientId}`,
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No integration configured
      }
      throw error;
    }
  },

  /**
   * Save/Update integration credentials (Admin only)
   * @param {Object} integrationData - Integration data
   * @param {string} integrationData.client_id - Client ID
   * @param {string} integrationData.atlassian_email - Jira email
   * @param {string} integrationData.atlassian_api_token - Jira API token
   * @param {string} integrationData.atlassian_domain - Jira domain
   * @param {string} integrationData.figma_api_token - Figma API token
   * @returns {Promise} Saved integration
   */
  saveIntegration: async (integrationData) => {
    const response = await integrationAPI.post("/config/save", integrationData);
    return response.data;
  },

  /**
   * Partially update integration credentials (Admin only)
   * @param {string} clientId - Client ID
   * @param {Object} updates - Fields to update
   * @returns {Promise} Updated integration
   */
  updateIntegration: async (clientId, updates) => {
    const response = await integrationAPI.patch(
      `/?client_id=${clientId}`,
      updates,
    );
    return response.data;
  },

  // Slack Integration Methods

  /**
   * Get Slack OAuth authorization URL
   * @param {string} clientId - Client ID (optional, used as state parameter)
   * @returns {Promise} OAuth URL
   */
  getSlackOAuthUrl: async (clientId = null) => {
    const url = clientId
      ? `/api/slack/oauth/url?client_id=${clientId}`
      : "/api/slack/oauth/url";
    const response = await integrationAPI.get(url);
    return response.data;
  },

  /**
   * Get Slack channels for a workspace
   * @param {string} workspaceId - Slack workspace ID
   * @param {string} clientId - Client ID (optional, for chief-admin)
   * @returns {Promise} List of channels
   */
  getSlackChannels: async (workspaceId, clientId = null) => {
    const url = clientId
      ? `/api/slack/channels/${workspaceId}?client_id=${clientId}`
      : `/api/slack/channels/${workspaceId}`;
    const response = await integrationAPI.get(url);
    return response.data;
  },

  /**
   * List all connected Slack workspaces
   * @param {string} clientId - Client ID (optional, for chief-admin)
   * @returns {Promise} List of workspaces
   */
  listSlackWorkspaces: async (clientId = null) => {
    const url = clientId
      ? `/api/slack/workspaces?client_id=${clientId}`
      : `/api/slack/workspaces`;
    const response = await integrationAPI.get(url);
    return response.data;
  },

  /**
   * Get Slack workspace details
   * @param {string} workspaceId - Slack workspace ID
   * @param {string} clientId - Client ID (optional, for chief-admin)
   * @returns {Promise} Workspace details
   */
  getSlackWorkspace: async (workspaceId, clientId = null) => {
    const url = clientId
      ? `/api/slack/workspace/${workspaceId}?client_id=${clientId}`
      : `/api/slack/workspace/${workspaceId}`;
    const response = await integrationAPI.get(url);
    return response.data;
  },

  /**
   * Save workspace for a client
   * @param {Object} data - Workspace data
   * @param {string} data.workspace_id - Workspace ID
   * @param {string} data.client_id - Client ID
   * @returns {Promise} Save result
   */
  saveClientWorkspace: async (data) => {
    const response = await integrationAPI.post(
      "/api/slack/save-workspace",
      data,
    );
    return response.data;
  },

  /**
   * Update Slack channel preference
   * @param {Object} data - Channel preference data
   * @param {string} data.workspace_id - Workspace ID
   * @param {string} data.client_id - Client ID
   * @param {string} data.notification_type - Notification type
   * @param {string} data.channel_id - Channel ID
   * @returns {Promise} Updated preference
   */
  updateChannelPreference: async (data) => {
    const response = await integrationAPI.post(
      "/api/slack/channel-preference",
      data,
    );
    return response.data;
  },

  /**
   * Join a Slack channel
   * @param {Object} data - Join channel data
   * @param {string} data.workspace_id - Workspace ID
   * @param {string} data.channel_id - Channel ID
   * @returns {Promise} Join result
   */
  joinSlackChannel: async (data) => {
    const response = await integrationAPI.post("/api/slack/join-channel", data);
    return response.data;
  },

  /**
   * Send Slack notification (for testing)
   * @param {Object} data - Notification data
   * @returns {Promise} Notification result
   */
  sendSlackNotification: async (data) => {
    const response = await integrationAPI.post("/notifications/send", data);
    return response.data;
  },

  // Jira Integration Methods

  /**
   * Get Jira projects for a client
   * @param {string} clientId - Client ID
   * @returns {Promise} List of Jira projects
   */
  getJiraProjects: async (clientId) => {
    const response = await integrationAPI.get(
      `/api/jira/projects?client_id=${clientId}`,
    );
    return response.data;
  },

  /**
   * Get Jira users assignable to a project (for assignee dropdown)
   * @param {string} clientId - Client ID
   * @param {string} projectKey - Jira project key (e.g. PROJ)
   * @param {string} query - Optional search string (name/email)
   * @returns {Promise} { users: [{ accountId, displayName, emailAddress }] }
   */
  getJiraAssignableUsers: async (clientId, projectKey, query = "") => {
    const params = new URLSearchParams({
      client_id: clientId,
      project_key: projectKey,
    });
    if (query && String(query).trim()) params.set("query", query.trim());
    const response = await integrationAPI.get(
      `/api/jira/assignable-users?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * Get Jira labels (for dropdown; new labels can be added by typing)
   * @param {string} clientId - Client ID
   * @returns {Promise} { labels: string[] }
   */
  getJiraLabels: async (clientId) => {
    const response = await integrationAPI.get(
      `/api/jira/labels?client_id=${clientId}`,
    );
    return response.data;
  },

  /**
   * Get Jira boards for a project (for sprint dropdown)
   * @param {string} clientId - Client ID
   * @param {string} projectKey - Jira project key
   * @returns {Promise} { boards: [{ id, name, type }] }
   */
  getJiraBoardsForProject: async (clientId, projectKey) => {
    const response = await integrationAPI.get(
      `/api/jira/boards-for-project?client_id=${clientId}&project_key=${encodeURIComponent(projectKey)}`,
    );
    return response.data;
  },

  /**
   * Get Jira sprints for a board
   * @param {string} clientId - Client ID
   * @param {string|number} boardId - Board ID
   * @param {string} state - active,future | active,future,closed
   * @returns {Promise} { sprints: [{ id, name, state }] }
   */
  getJiraSprints: async (clientId, boardId, state = "active,future") => {
    const response = await integrationAPI.get(
      `/api/jira/sprints?client_id=${clientId}&board_id=${encodeURIComponent(boardId)}&state=${encodeURIComponent(state)}`,
    );
    return response.data;
  },

  /**
   * Get Jira boards for a client
   * @param {string} clientId - Client ID
   * @returns {Promise} List of Jira boards
   */
  getJiraBoards: async (clientId) => {
    const response = await integrationAPI.get(
      `/api/jira/boards?client_id=${clientId}`,
    );
    return response.data;
  },

  /**
   * Get Jira issue details
   * @param {string} issueKey - Issue key (e.g., PROJ-123)
   * @param {string} clientId - Client ID
   * @returns {Promise} Issue details
   */
  getJiraIssue: async (issueKey, clientId) => {
    const response = await integrationAPI.get(
      `/api/jira/issues/${issueKey}?client_id=${clientId}`,
    );
    return response.data;
  },

  /**
   * Create Jira ticket
   * @param {Object} ticketData - Ticket data
   * @param {string} ticketData.client_id - Client ID
   * @param {string} ticketData.project_key - Project key
   * @param {string} ticketData.summary - Issue summary
   * @param {string} ticketData.description - Issue description
   * @param {string} ticketData.issue_type - Issue type
   * @param {string} ticketData.priority - Priority
   * @param {File[]} ticketData.attachments - Optional attachments
   * @returns {Promise} Created issue
   */
  createJiraTicket: async (ticketData) => {
    const formData = new FormData();
    Object.keys(ticketData).forEach((key) => {
      if (key === "attachments" && Array.isArray(ticketData[key])) {
        ticketData[key].forEach((file) => formData.append("attachments", file));
      } else if (key === "labels" && Array.isArray(ticketData[key])) {
        formData.append(key, ticketData[key].join(","));
      } else if (ticketData[key] !== undefined && ticketData[key] !== null) {
        formData.append(key, ticketData[key]);
      }
    });

    const response = await integrationAPI.post("/api/jira/tickets", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  /**
   * Update Jira issue
   * @param {string} issueKey - Issue key
   * @param {Object} updates - Fields to update
   * @param {string} clientId - Client ID
   * @returns {Promise} Updated issue
   */
  updateJiraIssue: async (issueKey, updates, clientId) => {
    const response = await integrationAPI.put(`/api/jira/issues/${issueKey}`, {
      client_id: clientId,
      ...updates,
    });
    return response.data;
  },

  /**
   * Add comment to Jira issue
   * @param {string} issueKey - Issue key
   * @param {string} comment - Comment text
   * @param {string} clientId - Client ID
   * @returns {Promise} Comment result
   */
  addJiraComment: async (issueKey, comment, clientId) => {
    const response = await integrationAPI.post(
      `/api/jira/issues/${issueKey}/comments`,
      {
        client_id: clientId,
        comment,
      },
    );
    return response.data;
  },

  /**
   * Search Jira issues using JQL
   * @param {string} jql - Jira Query Language query
   * @param {string} clientId - Client ID
   * @param {number} maxResults - Maximum results (default: 50)
   * @returns {Promise} Search results
   */
  searchJiraIssues: async (jql, clientId, maxResults = 50) => {
    const response = await integrationAPI.post("/issues/search", {
      client_id: clientId,
      jql,
      maxResults,
    });
    return response.data;
  },

  // Database Integration Methods

  /**
   * Get all database connections for a client
   * @param {string} clientId - Client ID
   * @returns {Promise} List of database connections
   */
  getDatabaseConnections: async (clientId) => {
    const response = await integrationAPI.get(
      `/api/database/connections?client_id=${clientId}`,
    );
    return response.data;
  },

  /**
   * Get a specific database connection
   * @param {string} connectionId - Connection ID
   * @param {string} clientId - Client ID
   * @returns {Promise} Connection details
   */
  getDatabaseConnection: async (connectionId, clientId) => {
    const response = await integrationAPI.get(
      `/api/database/connection?id=${connectionId}&client_id=${clientId}`,
    );
    return response.data;
  },

  /**
   * Save or update a database connection
   * @param {Object} connectionData - Connection data
   * @param {string} connectionData.client_id - Client ID
   * @param {string} connectionData.connection_name - Connection name
   * @param {string} connectionData.database_type - Database type (postgresql, mysql, oracle, mongodb, tidb)
   * @param {string} connectionData.host - Database host
   * @param {number} connectionData.port - Database port
   * @param {string} connectionData.database_name - Database name
   * @param {string} connectionData.username - Username
   * @param {string} connectionData.password - Password
   * @param {string} connectionData.connection_string - Optional connection string
   * @param {boolean} connectionData.ssl_enabled - SSL enabled
   * @param {string} connectionData.ssl_cert - SSL certificate
   * @param {Object} connectionData.additional_params - Additional parameters
   * @returns {Promise} Saved connection
   */
  saveDatabaseConnection: async (connectionData) => {
    const response = await integrationAPI.post(
      "/api/database/connections",
      connectionData,
    );
    return response.data;
  },

  /**
   * Test a database connection
   * @param {string} connectionId - Connection ID
   * @param {string} clientId - Client ID
   * @returns {Promise} Test result
   */
  testDatabaseConnection: async (connectionId, clientId) => {
    const response = await integrationAPI.post(
      `/api/database/connections/test?id=${connectionId}&client_id=${clientId}`,
    );
    return response.data;
  },

  /**
   * Delete a database connection
   * @param {string} connectionId - Connection ID
   * @param {string} clientId - Client ID
   * @returns {Promise} Delete result
   */
  deleteDatabaseConnection: async (connectionId, clientId) => {
    const response = await integrationAPI.delete(
      `/api/database/connections?id=${connectionId}&client_id=${clientId}`,
    );
    return response.data;
  },
};

export default integrationService;
